import {
    Backdrop,
    Box,
    CircularProgress,
    CloseOutlinedIcon,
    EditOutlinedIcon,
    FormatListBulletedOutlinedIcon,
    FormControl,
    IconButton,
    Menu,
    SearchOutlinedIcon,
    Stack,
    StackProps,
    styled,
    SvgIconComponent,
    Switch,
    SwitchProps,
    TextField,
    Tooltip,
    TuneOutlinedIcon,
    Typography,
} from '@ds/coolshop';
import {
    ChangeEventHandler,
    ComponentProps,
    createContext,
    forwardRef,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

const SidebarContext = createContext({ loading: false });

// The loading backdrop only shows for loads that take a while: a list that arrives within this
// delay is swapped in place without a spinner blinking over it.
const BACKDROP_DELAY_MS = 200;

const useDelayedFlag = (flag: boolean, delayMs: number): boolean => {
    const [delayed, setDelayed] = useState(false);

    useEffect(() => {
        if (!flag) {
            setDelayed(false);
            return undefined;
        }
        const timeoutId = setTimeout(() => setDelayed(true), delayMs);
        return () => clearTimeout(timeoutId);
    }, [flag, delayMs]);

    return delayed;
};

/**
 * Default width of the ThreeViewLayout left drawer hosting a PimSideBar. Consumers read the
 * effective width through `usePimSidebarWidth`, which lets `theme.sidebarWidth` override it.
 */
export const PIM_SIDEBAR_WIDTH = 'max(25%, 300px)';

const useSidebarContext = () => {
    const context = useContext(SidebarContext);

    if (!context)
        throw new Error(
            'You are using a <PimSideBar... /> component outside the <PimSideBar /> provider',
        );

    return context;
};

type PimSideBarProps = {
    loading?: boolean;
    topComponent: ReactNode;
    bottomComponent: ReactNode;
};

export const PimSideBar = ({ loading, topComponent, bottomComponent }: PimSideBarProps) => {
    // The delayed flag drives both the backdrop and the toggles' disabled state, so a quick
    // reload does not flash either of them.
    const showBackdrop = useDelayedFlag(Boolean(loading), BACKDROP_DELAY_MS);
    const sidebarContextValue = useMemo(() => ({ loading: showBackdrop }), [showBackdrop]);
    return (
        <SidebarContext.Provider value={sidebarContextValue}>
            {/* Fills its host: the ThreeViewLayout left drawer (which sizes it and casts the
                shadow over the content) or a dialog. */}
            <Box bgcolor="white" height="100%" width="100%" position="relative">
                <Backdrop open={showBackdrop} position="absolute" zIndex={10}>
                    <CircularProgress />
                </Backdrop>
                <Stack height="100%" gap={1}>
                    <Stack gap={2} paddingInline={3} paddingBlock={2}>
                        {topComponent}
                    </Stack>
                    <Box overflowY="auto" overflowX="hidden">
                        {/* Keeps the last row off the bottom edge at the end of the scroll. */}
                        <Box pb={2}>{bottomComponent}</Box>
                    </Box>
                </Stack>
            </Box>
        </SidebarContext.Provider>
    );
};

// The DS TextField paints its StartIcon with text.secondary through `htmlColor`; the search
// field wants it primary, so the color is fixed here and the injected one dropped.
const PrimarySearchIcon = Object.assign(
    ({ htmlColor, ...props }: ComponentProps<typeof SearchOutlinedIcon>) => (
        <SearchOutlinedIcon {...props} color="primary" />
    ),
    { muiName: 'SvgIcon' },
) as SvgIconComponent;

// The class is doubled to outrank the theme's normal-weight placeholder rule.
const SearchField = styled(TextField)({
    '& .MuiInputBase-input.MuiInputBase-input::placeholder': { fontWeight: 700 },
});

type PimSideBarSearchProps = {
    value: string | undefined;
    onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    disabled?: boolean;
    /** Filter controls (e.g. switches) shown inside the field, after the clear button. */
    filters?: ReactNode;
};

export const PimSideBarSearch = ({ value, onChange, disabled, filters }: PimSideBarSearchProps) => {
    const intl = useIntl();

    const clearButton = value ? (
        <IconButton onClick={() => onChange({ target: { value: '' } } as any)} size="small">
            <CloseOutlinedIcon fontSize="small" />
        </IconButton>
    ) : undefined;

    return (
        <FormControl fullWidth>
            <SearchField
                disabled={disabled}
                placeholder={intl.formatMessage({
                    id: 'search',
                    defaultMessage: 'Search',
                })}
                onChange={ev => onChange(ev)}
                StartIcon={PrimarySearchIcon}
                value={value}
                endAdornment={
                    clearButton || filters ? (
                        <Stack direction="row" alignItems="center" gap={2}>
                            {clearButton}
                            {filters}
                        </Stack>
                    ) : undefined
                }
            />
        </FormControl>
    );
};

type PimSideBarFiltersMenuProps = {
    /** Filter controls (e.g. `fullWidth` `PimSideBarToggle`s) listed vertically inside the menu. */
    children: ReactNode;
};

/**
 * Filter icon for the search field's `filters` slot: opens a menu that lists the given filter
 * controls, so the field keeps a single compact adornment. The menu stays open while the filters
 * are toggled and closes on outside click.
 */
export const PimSideBarFiltersMenu = ({ children }: PimSideBarFiltersMenuProps) => {
    const intl = useIntl();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const title = intl.formatMessage({
        id: 'sidebar-search.filters-menu.tooltip',
        defaultMessage: 'Search filters',
    });

    return (
        <>
            <Tooltip title={title}>
                <IconButton
                    size="small"
                    color="primary"
                    aria-label={title}
                    onClick={e => setAnchorEl(e.currentTarget)}
                >
                    <TuneOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Stack gap={1} paddingInline={2} paddingBlock={1}>
                    {children}
                </Stack>
            </Menu>
        </>
    );
};

type PimSideBarElementProps = {
    selected?: boolean;
    stackProps?: StackProps;
    onClick?: () => void;
};

export const PimSideBarElement = forwardRef<
    HTMLDivElement,
    Required<PropsWithChildren> & PimSideBarElementProps
>(({ selected, stackProps, onClick, children }, ref) => (
    <Stack
        ref={ref}
        paddingBlock={1}
        paddingInline={3}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={onClick}
        cursor="pointer"
        {...(selected ? { bgcolor: t => t.palette.grey[300] } : {})}
        {...stackProps}
    >
        {children}
    </Stack>
));

type PimSideBarActionType = 'edit' | 'children' | 'custom';

type PimSideBarEditAction = {
    type: Extract<PimSideBarActionType, 'edit'>;
};

type PimSideBarChildrenAction = {
    type: Extract<PimSideBarActionType, 'children'>;
};

type PimSideBarCustomAction = {
    type: Extract<PimSideBarActionType, 'custom'>;
    element: ReactElement;
};

type PimSideBarStandardAction = PimSideBarEditAction | PimSideBarChildrenAction;

type PimSideBarAction = {
    show?: boolean;
    disabled?: boolean;
    onClick?: () => void;
} & (PimSideBarStandardAction | PimSideBarCustomAction);

type PimSideBarElementActionsProps = {
    actions: PimSideBarAction[];
};

export const PimSideBarElementActions = ({ actions }: PimSideBarElementActionsProps) => {
    const visibleActions = actions.filter(action => action.show);

    return (
        <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
            {visibleActions.map(action => {
                if (action.type === 'edit')
                    return (
                        <IconButton
                            key={action.type}
                            aria-label="edit"
                            size="small"
                            color="primary"
                            disabled={action.disabled}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    );

                if (action.type === 'children')
                    return (
                        <IconButton
                            key={action.type}
                            aria-label="show children"
                            size="small"
                            onClick={e => {
                                e.stopPropagation();
                                action.onClick?.();
                            }}
                            color="primary"
                        >
                            <FormatListBulletedOutlinedIcon fontSize="small" />
                        </IconButton>
                    );

                return action.element;
            })}
        </Stack>
    );
};

type PimSideBarToggleProps = {
    label: string;
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
    size?: SwitchProps['size'];
    /** Fills the row, with the label on the left edge and the switch on the right one. */
    fullWidth?: boolean;
};

/**
 * Caption label followed by a switch, without the outer margins of a FormControlLabel. The DS
 * Switch keeps a 12px inner gutter past its track: the negative right margin makes the toggle's
 * box end at the track, so neighbours can be spaced from what is actually visible.
 * Unless `disabled` is given, the toggle follows the sidebar's (delayed) loading state.
 */
export const PimSideBarToggle = ({
    label,
    checked,
    onChange,
    disabled,
    size = 'small',
    fullWidth,
}: PimSideBarToggleProps) => {
    const { loading } = useSidebarContext();
    const isDisabled = disabled ?? loading;

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent={fullWidth ? 'space-between' : undefined}
            gap={0.5}
            mr={-1.5}
        >
            <Box
                cursor={isDisabled ? 'default' : 'pointer'}
                onClick={() => {
                    if (!isDisabled) onChange();
                }}
            >
                <Typography variant="caption" color={isDisabled ? 'text.disabled' : 'text.primary'}>
                    {label}
                </Typography>
            </Box>
            <Switch size={size} checked={checked} disabled={isDisabled} onChange={onChange} />
        </Stack>
    );
};

export const PimSideBarDisabledFilter = ({
    onChange,
    state,
    fullWidth,
}: {
    onChange: (checked: boolean) => void;
    state?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    fullWidth?: boolean;
}) => {
    const intl = useIntl();

    const [checked, setChecked] = state || useState(true);

    const handleChange = () => {
        setChecked(!checked);
        onChange(!checked);
    };

    return (
        <PimSideBarToggle
            checked={checked}
            onChange={handleChange}
            fullWidth={fullWidth}
            label={intl.formatMessage({
                id: 'hide-disabled.toggle',
                defaultMessage: 'Hide disabled',
            })}
        />
    );
};
